import { noticeError } from '@ratehub/base-ui';

import CreditCardShape from '../definitions/CreditCardShape';
import DEFAULT_CARD_PREFERENCES from '../definitions/DefaultCardPreferences';
import fetchCreditCard from '../functions/fetchCreditCard';
import verifyCardIntegrity from '../functions/verifyCardIntegrity';

/**
 * Returns a 'cardInfo' CreditCardShape given a slug or id that is
 * expected to pass verifyCardIntegrity.
 * @param {object} params - parameters object
 * @param {string} [params.slug] - the target card’s slug
 * @param {number} [params.id] - the target card’s id (only one of slug or id is required)
 * @param {string} [params.productType] - product type to verify against
 * @param {string} [params.languageCode] - 'en' | 'fr'
 * @param {string} [params.province] - the province code (e.g. 'ON')
 * @param {object} [params.userPreferences=DEFAULT_CARD_PREFERENCES] - contextual user information, typically as provided to CardFinder wizard
 * @param {boolean} [params.isDraftMode] - returns the latest draft version of the card
 * @param {string} params.profileId - profile id from session store
 * @param {string} params.documentId - recent CC journey document id
 * @param {boolean} params.isUsingApprovalModel - should we use the approval ML model?
 * @returns {Promise<CreditCardShape|null>} - CreditCardShape structure when successful, including verification
 */
async function fetchCardInfo({
    slug,
    id,
    productType,
    languageCode,
    province,
    userPreferences = DEFAULT_CARD_PREFERENCES,
    isDraftMode,
    profileId,
    documentId,
    isUsingApprovalModel,
}) {
    const card = await fetchCreditCard({
        slug,
        id,
        language: languageCode,
        province,
        userPreferences,
        isDraftMode,
        profileId,
        documentId,
        isUsingApprovalModel,
    });

    if (card) {
        if (verifyCardIntegrity(card, productType || card.productType)) {
            return card;
        } else {
            noticeError(
                new Error(`[fetchCardInfo] Card validity check failed for “${card?.name}”`),
                {
                    cardId: id,
                    cardResponse: card,
                    slug,
                    productType,
                    languageCode,
                    isDraftMode,
                },
            );

            return null;
        }
    }

    noticeError(
        new Error(`[fetchCardInfo] Failed to create card for “${id}” | “${slug}”`),
        {
            cardId: id,
            cardResponse: card,
            slug,
            productType,
            languageCode,
            isDraftMode,
        },
    );

    return null;
}


export default fetchCardInfo;
